import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { getRelativeURLWithParams } from 'utils/url';
import { REQUEST_ID_HEADER } from 'constants/headers';
import { getRequestId } from 'utils/requestId';
import { Method } from 'constants/enums';
import {
  PRICE_AND_AVAILABILITY,
  PRICE_AND_AVAILABILITY_HEADLESS
} from 'constants/endpoints.api';
import {
  transformPriceAndAvailability,
  transformPriceAndAvailabilityHeadless
} from 'services/dealerPriceAvailability/utils';
import { PartList } from 'services/availabilityBySource/types';
import { PriceAvailabilityResponse } from './types';

/*
      TODO - Input Validation - Query params
      orderId: Should be an alpha numeric string with no special characters.
      landId: negative number (ex. -1).
      partsList:  an array of alphanumeric strings restricting generalRestrictedCharacters (@\constants\securityConstants.ts).
      locationCode: alphanumeric strings restricting generalRestrictedCharacters (@\constants\securityConstants.ts).
  */
export const usePriceAndAvailabilityQuery = ({
  initialData,
  storeId,
  langId,
  partsList,
  locationCode = null,
  isHeadlessPriceAvailability = false,
  isDefaultResponseStructure = false,
  ...rest
}: {
  initialData?: PriceAvailabilityResponse;
  storeId: string;
  langId: string;
  partsList: PartList[];
  locationCode?: string;
  isHeadlessPriceAvailability?: boolean;
  isDefaultResponseStructure?: boolean;
} & Partial<UseQueryOptions<PriceAvailabilityResponse>>) => {
  const parts = partsList.length > 0 ? JSON.stringify(partsList) : '';

  return useQuery({
    initialData,
    queryKey: [
      'priceAndAvailability',
      locationCode ?? '',
      storeId,
      langId,
      parts,
      isHeadlessPriceAvailability,
      isDefaultResponseStructure
    ],
    queryFn: async () => {
      const priceUrl = isHeadlessPriceAvailability
        ? PRICE_AND_AVAILABILITY_HEADLESS
        : PRICE_AND_AVAILABILITY;
      const url = getRelativeURLWithParams(priceUrl, {
        storeId,
        langId,
        ...(!!locationCode && { locationCode })
      });
      const res = await fetch(url, {
        headers: { [REQUEST_ID_HEADER]: getRequestId() },
        method: Method.POST,
        body: parts
      });
      if (res.ok) {
        const response = await res.json();
        // this is a temporary change, once we migrate the component will remove this flag
        if (isDefaultResponseStructure) {
          return response;
        }
        const result = isHeadlessPriceAvailability
          ? transformPriceAndAvailabilityHeadless(response)
          : transformPriceAndAvailability(response);
        return result;
      } else {
        throw new Error('server error');
      }
    },
    enabled: parts.length > 0,
    ...rest
  });
};
